import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Upload from './uploadSteps/Upload.js'
import { useNavigate, useParams } from "react-router-dom";
import ImportPreview from "./uploadSteps/ImportPreview.js";
import Confirmation from "./uploadSteps/Confirmation.js";
import DefMembershipActions from '../../Redux/actions/defMembership-actions.js';
import BusinessCustomersActions from "../../Redux/transition/customers/business-customer.reducer.js";
import { useSelector, useDispatch } from "react-redux";
import Loading from "../../Components/Loading";
import { stripBom, findPhoneColumnKey, normalizeImportRows, getPhoneFromRow } from "../../utils/importCsvUtils";

export const ImportFileUpload = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { id: audienceId } = useParams();
  const dispatch = useDispatch();

  const defMembership = useSelector((state) => state.defMembership.defMembership);
  const { audience, fetchingAudience } = useSelector((state) => state.businessCustomers);

  const getMembership = useCallback(() => { dispatch(DefMembershipActions.defMembershipRequest()) }, [dispatch]);
  const getAudience = useCallback((id) => dispatch(BusinessCustomersActions.audienceRequest(id)), [dispatch]);
  const importBusinessCustomers = useCallback(
    (businessId, uploadAudienceId, file) =>
      dispatch(BusinessCustomersActions.businessCustomerUploadRequest(businessId, uploadAudienceId, file)),
    [dispatch]
  );

  const audienceName = audience?.importName || audience?.name || "";

  const [activeStep, setActiveStep] = useState(0);
  const [filteredRowsNoZero, setFilteredRowsNoZero] = useState([]);
  const [validFilteredRows, setValidFilteredRows] = useState([]);
  const [nonDuplicateNumbers, setNonDuplicateNumbers] = useState([]);
  const [duplicateNumbers, setDuplicateNumbers] = useState([]);
  const [state, setState] = useState({
    enterpriseCustomers: null,
    importedCustomers: [],
    uploadedCustomers: null,
    entries: null,
    file: {},
    count: null,
    page: 0,
    sort: "importedOn,desc",
    size: 20,
    loading: true,
    uploading: false,
    applying: false,
  })

  useEffect(() => {
    if (!defMembership) {
      getMembership();
      return;
    }
    if (audienceId) {
      getAudience(audienceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defMembership, audienceId]);

  const handleUpload = () => {
    let file = state.file;

    const reader = new FileReader();

    reader.onload = (event) => {
      let csvContent = event.target.result;
      if (typeof csvContent === "string") {
        csvContent = stripBom(csvContent);
      }
      const rows = csvContent.split("\n").filter(row => row.trim() !== "");
      const headers = rows[0].split(",").map((header) => stripBom(header.trim()));
      const phoneColumnKey = findPhoneColumnKey(headers);
      const dataRows = rows.slice(1).map(row => {
        const values = row.split(",");
        const rowObject = {};
        headers.forEach((header, index) => {
          rowObject[header] = values[index]?.trim();
        });
        return rowObject;
      });
      const normalizedRows = normalizeImportRows(dataRows, phoneColumnKey);

      const startingWithZero = normalizedRows.filter(row => {
        const phoneNumber = getPhoneFromRow(row, phoneColumnKey);
        return phoneNumber && phoneNumber.startsWith('0');
      });
      setValidFilteredRows(startingWithZero)

      const phoneCountMap = startingWithZero.reduce((acc, row) => {
        const phoneNumber = getPhoneFromRow(row, phoneColumnKey);

        if (phoneNumber) {
          acc[phoneNumber] = (acc[phoneNumber] || 0) + 1;
        }
        return acc;
      }, {});

      const nonDuplicatesOnly = startingWithZero.filter(row => {
        const phoneNumber = getPhoneFromRow(row, phoneColumnKey);
        return phoneCountMap[phoneNumber] === 1;
      });
      setNonDuplicateNumbers(nonDuplicatesOnly);

      const duplicatesOnly = startingWithZero.filter(row => {
        const phoneNumber = getPhoneFromRow(row, phoneColumnKey);
        return phoneCountMap[phoneNumber] !== 1;
      });
      setDuplicateNumbers(duplicatesOnly)

      const notStartingWithZero = normalizedRows.filter(row => {
        const phoneNumber = getPhoneFromRow(row, phoneColumnKey);
        return phoneNumber && !phoneNumber.startsWith('0');
      });
      setFilteredRowsNoZero(notStartingWithZero);
    };

    reader.readAsText(file);

  };

  const handleContinue = () => {
    if (activeStep === 2) {
      setActiveStep(0)
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setState((state) => ({ ...state, file: {} }))
    if (activeStep === 2) {
      setActiveStep(0)
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const navigateToAudienceDetail = () => navigate(`/crm/audiences/audience/${audienceId}`);

  const steps = [
    { label: 'business_customers.upload', icon: "pi-upload" },
    { label: 'business_customers.import_preview', icon: "pi-search" },
    { label: 'business_customers.complete', icon: "pi-check-circle" }
  ];

  if (!defMembership || (fetchingAudience && !audience)) {
    return <Loading />;
  }

  return (
    <div>
      <div className="card card-div">
        <div className='fav-card-content'>
          <ul className="list-none p-0 m-0 flex flex-column md:flex-row ">
            {steps.map(({ label, icon }, i) => (
              <li key={i} className={`relative ${i + 1 !== steps.length ? "mr-0 md:mr-8" : ""}  flex-auto`}>
                <div className={`step-box step-box-${activeStep === i ? "active" : i < activeStep ? "pre" : "post"}`}>
                  <i className={`pi step-box-icon ${activeStep === i ? "step-box-active-icon " + icon : i < activeStep ? "step-box-pre-icon pi-check-circle" : "step-box-post-icon " + icon}`}></i>
                  <div>
                    <div className={`step-box-title step-box-${activeStep === i ? "active" : i < activeStep ? "pre" : "post"}-title`}>{`${i + 1 === steps.length ? t("totalTransfers.success") : t("totalTransfers.step", {val: i + 1 })}`}</div>
                    <span className={`step-box-des step-box-${activeStep === i ? "active" : i < activeStep ? "pre" : "post"}-des  hidden md:block`}>{t(label)}</span>
                  </div>
                </div>
                {i + 1 !== steps.length && <div className="hidden md:block step-box-bar" ></div>}
              </li>))}
          </ul>
        </div>

      </div>
      <div>
        {activeStep === 0 && (
          <Upload
            {...{
              handleContinue,
              handleUpload,
              navigateToImportPage: navigateToAudienceDetail,
              state,
              setState,
              audienceName,
            }}
          />
        )}
        {activeStep === 1 && (
          <ImportPreview
            {...{
              handleBack,
              handleContinue,
              navigateToImportPage: navigateToAudienceDetail,
              validFilteredRows,
              filteredRowsNoZero,
              nonDuplicateNumbers,
              importBusinessCustomers,
              defMembership,
              state,
              setState,
              duplicateNumbers,
              audienceId,
              audienceName,
            }}
          />
        )}
        {activeStep === 2 && (
          <Confirmation
            {...{
              validFilteredRows,
              nonDuplicateNumbers,
              duplicateNumbers,
              navigateToImportPage: navigateToAudienceDetail,
              state,
              setState,
              handleBack,
              audienceName,
              audienceId,
            }}
          />
        )}
      </div>
    </div>
  )
}
